import clsx from 'clsx'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import Button from '@/components-latest/primitives/Button'
import CircularButton from '@/components-latest/primitives/CircularButton'
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter
} from '@/components-latest/primitives/Drawer'
import { Cross, CashPayment } from '@/components-latest/primitives/Icons'
import ScrollArea from '@/components-latest/primitives/Scrollable'
import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import OrderStatus from '@/components-latest/ui/OrdersPage/OrderStatus/OrderStatus'
import { SupportedLanguages } from '@/config/i18n'
import { useLazyFetchStoreWalletFundingAttemptQuery } from '@/ducks/billing/api/billing.api'
import {
  WalletTransactionDetailsPaymentMethod,
  WalletTransactionDetailsViewModel
} from '@/ducks/billing/billing.types'
import {
  copyWalletTextToClipboard,
  getWalletCashOutStatusLabelKey,
  isCashOutWalletTransactionType,
  isFailedFundingAttemptState,
  isSettledWalletCashOutStatus,
  resolveWalletAgentInstructions,
  resolveWalletListTransactionStatus,
  shouldShowExpiredWalletAgentCodeHint,
  shouldShowWalletAgentCode
} from '@/ducks/billing/billing.wallet-funding'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import { OrderStatuses } from '@/ducks/orders/orders.types'
import { formatPrice } from '@/utils/currency.utils'

import Styles from './TransactionDetailsDrawer.module.scss'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  details?: WalletTransactionDetailsViewModel
  listTransaction?: {
    status: WalletTransactionStatus
    type: string
    cashOutDisplayStatus?: string
  }
  fundingAttemptId?: string
  isLoading?: boolean
  isError?: boolean
  formatDateTime: (value: string) => string
}

type WalletTransactionStatus =
  | 'pending'
  | 'done'
  | 'refund'
  | 'failed'
  | 'canceled'

const mapWalletTransactionStatus = (
  status: string | undefined
): WalletTransactionStatus => {
  const normalizedStatus = String(status || '').toUpperCase()

  if (normalizedStatus === 'CONFIRMED' || normalizedStatus === 'DONE') {
    return 'done'
  }
  if (normalizedStatus === 'REFUND' || normalizedStatus === 'REFUNDED') {
    return 'refund'
  }
  if (normalizedStatus === 'FAILED') {
    return 'failed'
  }
  if (
    normalizedStatus === 'CANCELLED' ||
    normalizedStatus === 'CANCELED'
  ) {
    return 'canceled'
  }

  return 'pending'
}

const isCashPaymentMethod = (
  paymentMethod?: WalletTransactionDetailsPaymentMethod
) => {
  if (!paymentMethod) return false

  const labels = [
    paymentMethod.displayName,
    ...Object.values(paymentMethod.displayNameI18n || {})
  ]
    .filter(Boolean)
    .map(label => label.toUpperCase())

  if (
    labels.some(
      label =>
        label === 'CASH' ||
        label.includes('CASH') ||
        label.includes('ESPÈCES') ||
        label.includes('ESPECES')
    )
  ) {
    return true
  }

  return Boolean(paymentMethod.icon?.includes('money_cash'))
}

const mapWalletTransactionType = (transactionType: string | undefined) => {
  const normalizedType = String(transactionType || '').toUpperCase()
  if (normalizedType.includes('TOP_UP')) return 'Top up'
  if (normalizedType.includes('CASH_OUT')) return 'Cash out'
  if (normalizedType.includes('ORDER')) return 'Order'
  return transactionType || '-'
}

const shouldFetchFundingAttempt = (
  details?: WalletTransactionDetailsViewModel,
  fundingAttemptId?: string,
  listTransactionType?: string
) => {
  if (!fundingAttemptId) return false

  return (
    isCashOutWalletTransactionType(details?.transaction.transactionType) ||
    isCashOutWalletTransactionType(listTransactionType)
  )
}

const DetailRow = ({
  label,
  value,
  valueClassName,
  valueNode,
  alignValueStart = false
}: {
  label: string
  value?: string
  valueClassName?: string
  valueNode?: React.ReactNode
  alignValueStart?: boolean
}) => (
  <div
    className={clsx(Styles.summaryRow, {
      [Styles.summaryRowValueStart]: alignValueStart
    })}>
    <Text variant="nextSubtitle">{label}</Text>
    {!alignValueStart ? <div className={Styles.dots} /> : null}
    <div
      className={clsx({
        [Styles.summaryValueStart]: alignValueStart
      })}>
      {valueNode || (
        <Text
          variant="nextSubtitle"
          weight="bold"
          className={clsx(valueClassName)}>
          {value}
        </Text>
      )}
    </div>
  </div>
)

const WalletTransactionStatusBadge = ({
  status,
  label
}: {
  status: WalletTransactionStatus
  label: string
}) => (
  <span
    className={clsx(Styles.walletStatusTag, {
      [Styles.walletStatusPending]: status === 'pending',
      [Styles.walletStatusDone]: status === 'done',
      [Styles.walletStatusRefund]:
        status === 'refund' ||
        status === 'failed' ||
        status === 'canceled'
    })}>
    <span
      className={clsx(Styles.walletStatusDot, {
        [Styles.walletStatusDotPending]: status === 'pending',
        [Styles.walletStatusDotDone]: status === 'done',
        [Styles.walletStatusDotRefund]:
          status === 'refund' ||
          status === 'failed' ||
          status === 'canceled'
      })}
    />
    {label}
  </span>
)

const TransactionDetailsDrawer = ({
  open,
  onOpenChange,
  details,
  listTransaction,
  fundingAttemptId,
  isLoading = false,
  isError = false,
  formatDateTime
}: Props) => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const currentLanguage = i18n.language as SupportedLanguages

  const [
    getFundingAttempt,
    {
      data: fundingAttempt,
      isFetching: isFundingAttemptFetching,
      isError: isFundingAttemptError
    }
  ] = useLazyFetchStoreWalletFundingAttemptQuery()

  const effectiveFundingAttemptId =
    fundingAttemptId || details?.transaction.fundingAttemptId

  const shouldLoadFundingAttempt = shouldFetchFundingAttempt(
    details,
    effectiveFundingAttemptId,
    listTransaction?.type
  )

  const activeFundingAttempt =
    effectiveFundingAttemptId &&
    fundingAttempt?.fundingAttemptId === effectiveFundingAttemptId
      ? fundingAttempt
      : undefined

  const isFundingAttemptLoading =
    Boolean(effectiveFundingAttemptId) &&
    shouldLoadFundingAttempt &&
    !isFundingAttemptError &&
    (isFundingAttemptFetching ||
      fundingAttempt?.fundingAttemptId !== effectiveFundingAttemptId)

  useEffect(() => {
    if (!open || !shouldLoadFundingAttempt || !effectiveFundingAttemptId) {
      return
    }

    getFundingAttempt({ fundingAttemptId: effectiveFundingAttemptId })
  }, [
    effectiveFundingAttemptId,
    getFundingAttempt,
    open,
    shouldLoadFundingAttempt
  ])

  // Prefer details API once loaded — list can stay stale until refresh (OK).
  // Do not prefer list over details: after webhook, detail returns PAID/CONFIRMED
  // while the list row may still say Ready.
  const resolvedTransactionStatus = details?.transaction.status
    ? String(details.transaction.status).toUpperCase()
    : listTransaction
      ? resolveWalletListTransactionStatus(listTransaction.status)
      : ''

  const agentInstructions = resolveWalletAgentInstructions(activeFundingAttempt)
  const showAgentCode =
    shouldLoadFundingAttempt &&
    shouldShowWalletAgentCode({
      fundingAttempt: activeFundingAttempt,
      transactionStatus: resolvedTransactionStatus
    })
  const showFundingFailure =
    shouldLoadFundingAttempt &&
    isFailedFundingAttemptState(activeFundingAttempt) &&
    !isFundingAttemptLoading &&
    !isFundingAttemptError

  const settlementSettledAt = activeFundingAttempt?.settlement?.settledAt
  const showSettledAt =
    shouldLoadFundingAttempt &&
    isSettledWalletCashOutStatus(resolvedTransactionStatus) &&
    Boolean(settlementSettledAt)

  const showExpiredAgentCodeHint =
    shouldLoadFundingAttempt &&
    shouldShowExpiredWalletAgentCodeHint({
      fundingAttempt: activeFundingAttempt,
      transactionStatus: resolvedTransactionStatus
    })

  const handleCopyAgentCode = useCallback(async () => {
    const code = agentInstructions?.code
    if (!code) return

    const copied = await copyWalletTextToClipboard(code)
    dispatch(
      addSystemNotification({
        message: t(
          copied
            ? 'billing.wallet.payout.code-copied'
            : 'billing.wallet.payout.copy-failed'
        ),
        intent: copied ? 'info' : 'error',
        duration: 2000
      })
    )
  }, [agentInstructions?.code, dispatch, t])

  const paymentMethodLabel = useMemo(() => {
    const paymentMethod = details?.breakdown?.paymentMethod
    if (!paymentMethod) return '-'

    return (
      paymentMethod.displayNameI18n?.[currentLanguage] ||
      paymentMethod.displayName ||
      '-'
    )
  }, [currentLanguage, details?.breakdown?.paymentMethod])

  const orderStatusLabel = useMemo(() => {
    const status = details?.order?.status
    if (!status) return ''

    return status.titleI18n?.[currentLanguage] || status.title
  }, [currentLanguage, details?.order?.status])

  const mapCashOutDisplayToBadgeStatus = (
    cashOutDisplayStatus?: string
  ): WalletTransactionStatus | null => {
    const normalized = String(cashOutDisplayStatus || '').toUpperCase()
    if (normalized === 'PAID') return 'done'
    if (normalized === 'FAILED') return 'failed'
    if (normalized === 'READY') return 'pending'
    return null
  }

  const walletTransactionStatus: WalletTransactionStatus = details
    ? mapCashOutDisplayToBadgeStatus(details.transaction.cashOutDisplayStatus) ??
      mapWalletTransactionStatus(details.transaction.status)
    : listTransaction?.status ?? 'pending'
  const walletTransactionStatusLabel = t(
    getWalletCashOutStatusLabelKey({
      transactionType:
        details?.transaction.transactionType ?? listTransaction?.type,
      status: walletTransactionStatus,
      cashOutDisplayStatus:
        details?.transaction.cashOutDisplayStatus ??
        listTransaction?.cashOutDisplayStatus
    })
  )

  const currency = details?.transaction?.currency || ''
  const hasOrderBreakdown = Boolean(details?.order && details?.breakdown)
  const formattedDateTime = details?.transaction
    ? formatDateTime(details.transaction.transactionDateTime)
    : ''

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="wallet-transaction-details-drawer">
        <div className={Styles.topBar}>
          <DrawerClose
            asChild
            data-testid="wallet-transaction-details-close-icon">
            <CircularButton
              variant="flat"
              size="normal"
              aria-label={t('billing.wallet.details.close')}>
              <Cross />
            </CircularButton>
          </DrawerClose>
        </div>

        <ScrollArea>
          <DrawerBody className={Styles.body} data-vaul-no-drag>
            {isLoading ? (
              <div className={Styles.loadingWrapper}>
                <Spinner />
              </div>
            ) : isError ? (
              <div className={Styles.errorWrapper}>
                <Text variant="nextBody" align="center">
                  {t('billing.wallet.details.load-error')}
                </Text>
              </div>
            ) : details ? (
              <>
                <div className={Styles.orderHeaderRow}>
                  {details.order ? (
                    <Text variant="nextBody" weight="semiBold">
                      <span className={Styles.metaText}>
                        {t('billing.wallet.table.order-id')}:{' '}
                      </span>
                      <span className={Styles.orderId}>
                        {details.order.orderId}
                      </span>
                    </Text>
                  ) : (
                    <Text variant="nextBody" weight="semiBold">
                      {mapWalletTransactionType(
                        details.transaction.transactionType || listTransaction?.type
                      )}
                    </Text>
                  )}
                  <WalletTransactionStatusBadge
                    status={walletTransactionStatus}
                    label={walletTransactionStatusLabel}
                  />
                </div>

                <div className={Styles.section}>
                  <Text
                    variant="nextTitle-2"
                    weight="semiBold"
                    className={Styles.sectionTitle}>
                    {t('billing.wallet.details.general-details-title')}
                  </Text>

                  <DetailRow
                    label={t('billing.wallet.details.transaction-type')}
                    value={mapWalletTransactionType(
                      details.transaction.transactionType || listTransaction?.type
                    )}
                  />

                  {details.order?.status?.key ? (
                    <DetailRow
                      label={t('billing.wallet.details.order-status')}
                      valueNode={
                        <OrderStatus
                          assigned
                          status={
                            details.order?.status?.key as OrderStatuses
                          }>
                          {orderStatusLabel}
                        </OrderStatus>
                      }
                    />
                  ) : null}

                  {details.order?.customerName ? (
                    <DetailRow
                      label={t('billing.wallet.details.client-name')}
                      value={details.order.customerName}
                    />
                  ) : null}

                  <DetailRow
                    label={t('billing.wallet.table.date-time')}
                    value={formattedDateTime}
                  />
                </div>

                {shouldLoadFundingAttempt ? (
                  <>
                    <div className={Styles.sectionDivider} />
                    <div
                      className={Styles.section}
                      data-testid="wallet-transaction-funding-details">
                      {isFundingAttemptLoading ? (
                        <div className={Styles.fundingAttemptLoading}>
                          <Spinner />
                        </div>
                      ) : isFundingAttemptError ? (
                        <Text variant="nextCaption" className={Styles.validityHint}>
                          {t('billing.wallet.details.agent-code-load-error')}
                        </Text>
                      ) : (
                        <>
                          {showFundingFailure ? (
                            <Text
                              variant="nextBody"
                              className={Styles.fundingFailureMessage}
                              data-testid="wallet-transaction-funding-failure">
                              {t('billing.wallet.payout.errors.withdraw-failed')}
                            </Text>
                          ) : null}
                          {showAgentCode && agentInstructions ? (
                            <div data-testid="wallet-transaction-agent-code">
                              <Text
                                variant="nextTitle-2"
                                weight="semiBold"
                                className={Styles.sectionTitle}>
                                {agentInstructions.title ||
                                  t('billing.wallet.payout.instructions-title')}
                              </Text>
                              <Text
                                variant="nextBody"
                                className={Styles.instructionsBody}>
                                {agentInstructions.body ||
                                  t('billing.wallet.payout.instructions-body')}
                              </Text>
                              <div className={Styles.codeBlock}>
                                <Text
                                  variant="nextCaption"
                                  weight="semiBold"
                                  className={Styles.code}>
                                  {agentInstructions.code}
                                </Text>
                                <Button
                                  variant="tertiary"
                                  size="smaller"
                                  className={Styles.copyButton}
                                  onClick={handleCopyAgentCode}>
                                  {t('billing.wallet.payout.copy-code')}
                                </Button>
                              </div>
                              <Text
                                variant="nextCaption"
                                className={Styles.validityHint}>
                                {t('billing.wallet.payout.validity-hint')}
                              </Text>
                            </div>
                          ) : showExpiredAgentCodeHint ? (
                            <Text
                              variant="nextCaption"
                              className={Styles.validityHint}
                              data-testid="wallet-transaction-agent-code-expired">
                              {t('billing.wallet.details.agent-code-expired')}
                            </Text>
                          ) : null}

                          {showSettledAt && settlementSettledAt ? (
                            <div
                              className={clsx({
                                [Styles.settlementSection]: showAgentCode
                              })}
                              data-testid="wallet-transaction-settled-at">
                              <DetailRow
                                label={t('billing.wallet.details.settlement-at')}
                                value={formatDateTime(settlementSettledAt)}
                              />
                            </div>
                          ) : null}
                        </>
                      )}
                    </div>
                  </>
                ) : null}

                {hasOrderBreakdown ? <div className={Styles.sectionDivider} /> : null}

                {hasOrderBreakdown && details.breakdown ? (
                  <div className={Styles.section}>
                    <Text
                      variant="nextTitle-2"
                      weight="semiBold"
                      className={Styles.sectionTitle}>
                      {t('billing.wallet.details.billing-details-title')}
                    </Text>

                    <DetailRow
                      label={t('billing.wallet.details.order-amount')}
                      value={formatPrice(details.breakdown.subtotal, currency, {
                        signDisplay: 'never'
                      })}
                    />
                    <DetailRow
                      label={t('billing.wallet.details.yassir-commission')}
                      value={formatPrice(
                        -Math.abs(details.breakdown.commission),
                        currency,
                        { signDisplay: 'never' }
                      )}
                      valueClassName={Styles.negative}
                    />
                    <DetailRow
                      label={t('orders.discount')}
                      value={formatPrice(
                        -Math.abs(details.breakdown.discount),
                        currency,
                        { signDisplay: 'never' }
                      )}
                      valueClassName={Styles.negative}
                    />
                    <DetailRow
                      label={t('orders.payment-method')}
                      valueNode={
                        <span className={Styles.paymentMethodChip}>
                          {isCashPaymentMethod(
                            details.breakdown.paymentMethod
                          ) ? (
                            <CashPayment className={Styles.paymentMethodIconSvg} />
                          ) : details.breakdown.paymentMethod.icon ? (
                            <img
                              src={details.breakdown.paymentMethod.icon}
                              alt=""
                              className={Styles.paymentMethodIcon}
                            />
                          ) : null}
                          <span className={Styles.paymentMethodLabel}>
                            {paymentMethodLabel}
                          </span>
                        </span>
                      }
                    />
                    <div className={clsx(Styles.summaryRow, Styles.totalRow)}>
                      <Text variant="nextSubtitle" weight="semiBold">
                        {t('billing.wallet.details.total')}
                      </Text>
                      <div className={Styles.dots} />
                      <Text
                        variant="heading-05"
                        weight="bold"
                        className={Styles.positive}>
                        {formatPrice(details.breakdown.earnings, currency, {
                          signDisplay: 'never'
                        })}
                      </Text>
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}
          </DrawerBody>
        </ScrollArea>

        <DrawerFooter className={Styles.footer}>
          <DrawerClose asChild>
            <Button
              variant="branded"
              size="large"
              shape="rounded"
              className={Styles.closeButton}>
              {t('billing.wallet.details.close')}
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export default TransactionDetailsDrawer
